import { CheckCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Collapse,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { IPurchaseFilter, IPurchaseReceiptItem } from "../../models/Purchase";
import {
  useCreatePurchaseReceipt,
  useGetPurchaseRequest,
  useGetPurchaseRequestByAlias,
} from "../../query/PurchaseQuery";
import { notify } from "../../utils/toast";

const PurchaseReceiptCreate = () => {
  const [selectedPO, setSelectedPO] = useState<string>();
  const [filter, setFilter] = useState<IPurchaseFilter>({
    page: 1,
    limit: 100,
    pONumber: undefined,
  });
  const { data, isLoading } = useGetPurchaseRequest(filter);
  const {
    data: purchaseRequestDetail,
    isLoading: purchaseRequestDetailLoading,
  } = useGetPurchaseRequestByAlias(selectedPO);
  const createPurchaseReceipt = useCreatePurchaseReceipt();
  const [items, setItems] = useState<IPurchaseReceiptItem[]>([]);
  const [overallRemark, setOverallRemark] = useState<string>();

  useEffect(() => {
    if (purchaseRequestDetail?.items) {
      const mappedItems: IPurchaseReceiptItem[] =
        purchaseRequestDetail.items.map((item) => ({
          id: item.id,
          partNumber: item.partNumber,
          description: item.description,
          ordered: item.quantity,
          alreadyReceived: item.quantityReceived,
          remaining: item.remainingQuantity,
          receivingQty: 0,
          rejectingQty: 0,
          accepted: 0,
          qualityStatus: "GOOD",
          remark: "",
        }));
      setItems(mappedItems);
    } else if (!selectedPO) {
      setItems([]);
    }
  }, [purchaseRequestDetail, selectedPO]);

  const handleCreatePurchaseReceipt = async () => {
    if (!purchaseRequestDetail?.id) return;

    const payload = {
      purchaseRequestId: purchaseRequestDetail.id,
      receivedDate: new Date(),
      note: overallRemark,
      items: items
        .filter((item) => item.receivingQty > 0)
        .map((item) => ({
          purchaseRequestItemId: item.id,
          quantityReceived: item.receivingQty,
          qualityStatus: item.qualityStatus as "GOOD" | "DAMAGED" | "DEFECTIVE",
          rejectedQuantity: item.rejectingQty,
          remark: item.remark || undefined,
        })),
    };
    try {
      await createPurchaseReceipt.mutateAsync(payload);
      notify.success("Tạo phiếu nhận thành công");
    } catch (error) {
      notify.error("Tạo phiếu nhận thất bại");
    }
  };
  const updateItem = (
    id: number,
    field: keyof IPurchaseReceiptItem,
    value: any
  ) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "receivingQty" || field === "rejectingQty") {
            updated.accepted = updated.receivingQty - updated.rejectingQty;
          }
          return updated;
        }
        return item;
      })
    );
  };

  const renderItemCard = (item: IPurchaseReceiptItem) => {
    const collapseItems = [
      {
        key: item.id.toString(),
        label: (
          <Space>
            <Typography.Text strong>{item.partNumber}</Typography.Text>
            <Typography.Text type="secondary">
              - {item.description}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ marginLeft: 16 }}>
              Yêu cầu mua với số lượng: {item.ordered} - Đã nhận:{" "}
              {item.alreadyReceived} - Còn lại: {item.remaining}
            </Typography.Text>
          </Space>
        ),
        children: (
          <>
            <Card
              type="inner"
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: "#faad14" }} />
                  <Typography.Text>Đơn nhận này</Typography.Text>
                </Space>
              }
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-4">
                    <Typography.Text type="secondary">
                      Số lượng nhận
                    </Typography.Text>
                    <InputNumber
                      value={item.receivingQty}
                      onChange={(value) =>
                        updateItem(item.id, "receivingQty", value || 0)
                      }
                      min={0}
                      max={item.remaining}
                      style={{
                        width: "100%",
                        marginTop: 4,
                        borderColor: "#52c41a",
                      }}
                      size="large"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-4">
                    <Typography.Text type="secondary">
                      Số lượng từ chối
                    </Typography.Text>
                    <InputNumber
                      value={item.rejectingQty}
                      onChange={(value) =>
                        updateItem(item.id, "rejectingQty", value || 0)
                      }
                      min={0}
                      max={item.receivingQty}
                      style={{
                        width: "100%",
                        marginTop: 4,
                        borderColor: "#ff4d4f",
                      }}
                      size="large"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-4">
                    <Typography.Text type="secondary">
                      Chấp nhận (tự động)
                    </Typography.Text>
                    <InputNumber
                      value={item.accepted}
                      disabled
                      style={{ width: "100%", marginTop: 4 }}
                      size="large"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <div className="mb-4">
                    <Typography.Text type="secondary">
                      Trạng thái chất lượng
                    </Typography.Text>
                    <Select
                      value={item.qualityStatus}
                      onChange={(value) =>
                        updateItem(item.id, "qualityStatus", value)
                      }
                      style={{ width: "100%", marginTop: 4 }}
                      size="large"
                      options={[
                        { label: "Tốt", value: "GOOD" },
                        { label: "Hỏng hóc do vận chuyển", value: "DAMAGED" },
                        { label: "Lỗi kỹ thuật", value: "DEFECTIVE" },
                      ]}
                    />
                  </div>
                </Col>
                <Col xs={24} md={16} lg={7}>
                  <div className="mb-4">
                    <Typography.Text type="secondary">Ghi chú</Typography.Text>
                    <Input
                      value={item.remark}
                      onChange={(e) =>
                        updateItem(item.id, "remark", e.target.value)
                      }
                      placeholder="Thêm ghi chú"
                      style={{ marginTop: 4 }}
                      size="large"
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          </>
        ),
      },
    ];
    return (
      <Collapse
        key={item.id}
        items={collapseItems}
        className="mb-4"
        defaultActiveKey={item.receivingQty > 0 ? [item.id.toString()] : []}
      />
    );
  };

  return (
    <div className="p-4">
      <Card title="Vui lòng chọn mã đơn yêu cầu" className="mb-4">
        <Select
          showSearch
          placeholder="Chọn mã yêu cầu"
          value={selectedPO}
          onChange={setSelectedPO}
          onSearch={(value) => {
            setFilter((prev) => ({
              ...prev,
              pONumber: value || undefined,
            }));
          }}
          filterOption={false}
          loading={isLoading}
          style={{ width: "100%" }}
          size="large"
          options={
            data?.data?.map((po) => ({
              label: `${po.requestNumber} - ${po.projectName}`,
              value: po.requestNumber,
            })) || []
          }
          notFoundContent={isLoading ? <Spin /> : "Không tìm thấy"}
        />
      </Card>

      <Card title="Danh sách cần mua" className="mb-4">
        {purchaseRequestDetailLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
            Vui lòng chọn mã đơn yêu cầu
          </div>
        ) : (
          <>
            {items.map((item) => renderItemCard(item))}
            <Card title="Tổng quan" type="inner">
              <Input.TextArea
                value={overallRemark}
                onChange={(e) => setOverallRemark(e.target.value)}
                placeholder="Ghi chú thông tin chung"
                rows={4}
              />
            </Card>
          </>
        )}
      </Card>

      <Card>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button size="large">Huỷ</Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn tạo phiếu nhận này?"
            onConfirm={handleCreatePurchaseReceipt}
            okText="Tạo"
            cancelText="Huỷ"
            okButtonProps={{ loading: createPurchaseReceipt.isPending }}
            disabled={
              !selectedPO ||
              items.length === 0 ||
              items.every((item) => item.receivingQty === 0)
            }
          >
            <Button
              type="primary"
              size="large"
              disabled={
                !selectedPO ||
                items.length === 0 ||
                items.every((item) => item.receivingQty === 0)
              }
            >
              Tạo phiếu nhận
            </Button>
          </Popconfirm>
        </Space>
      </Card>
    </div>
  );
};

export default PurchaseReceiptCreate;
